/**
 * ONE mapping from the reschedule door's refusal codes to the message the practitioner
 * reads. `POST /admin/bookings/{id}/reschedule` answers 409 with a machine-readable
 * `detail`; the constants are declared in `modules/booking/service.py` (SAME_TIME …
 * RESCHEDULE_CONFLICT) and `modules/booking/availability.py` (TIME_UNAVAILABLE), and every
 * one of them is a different obstacle with a different remedy, so collapsing them into a
 * generic failure throws away the only useful part of the answer.
 *
 * This table lives on its own because TWO surfaces reach that door: the appointment's own
 * `RescheduleDialog`, and the calendar's drag-and-drop. A second copy of the table would
 * drift from this one the first time the server grows a refusal.
 *
 * The two OVERRIDABLE refusals (OUTSIDE_AVAILABILITY, TARGET_GCAL_BUSY) are deliberately
 * absent: the drag turns them into a confirm-and-retry, and their plain sentences live in
 * calendarUtils' ERROR_CODE_KEYS for every other surface.
 *
 * A code absent from this table is NOT an error in itself — callers fall back to
 * `errMsg`/`extractApiError`, never to a raw code shown to a human.
 */
export const RESCHEDULE_ERR_KEYS = {
  // Decided before anything moves.
  DELIVERY_MISMATCH: "reschedule.err.deliveryMismatch",
  SAME_TIME: "reschedule.err.sameTime",
  // The state of the appointment itself.
  BOOKING_CANCELLED: "reschedule.err.bookingCancelled",
  BOOKING_COMPLETED: "reschedule.err.bookingCompleted",
  PAYMENT_PENDING: "reschedule.err.paymentPending",
  NEEDS_ATTENTION: "reschedule.err.needsAttention",
  PAYMENT_REFUNDED: "reschedule.err.paymentRefunded",
  // The target time: another client holds one of its minutes (the claims refused it),
  // or it is not offered at all. Never overridable.
  TIME_UNAVAILABLE: "reschedule.err.timeUnavailable",
  // The appointment itself moved (another tab, the client's own self-service) between
  // this screen's read and the write.
  CONFLICT: "reschedule.err.conflict",
};

/**
 * The i18n key for a refusal, or `null` when the code is unknown (or the failure was not a
 * refusal at all — a network error carries no `detail`).
 */
export const rescheduleErrKey = (detail) =>
  (typeof detail === "string" ? RESCHEDULE_ERR_KEYS[detail] : null) || null;
